// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { hasChanges } from "@insite/client-framework/Store/Pages/AccountSettings/AccountSettingsSelectors";
import saveCurrentAccount from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SaveCurrentAccount";
import setInitialValues from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SetInitialValues";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@steiner/App/Components/Notify";
import xLoadingOverlay from "@steiner/App/Handlers/XNG/LoadingOverlay";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { hasCustomAccountFieldsChanges } from "@steiner/App/Store/Selectors/AccountSettingsSelectors";
import { GridContainerProps } from "@steiner/MobiusOverrides/GridContainer/GridContainer";
import { xParserOptions } from "@steiner/XDL/Utilities/BasicSelectors";
import parse from "html-react-parser";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    account: state.pages.accountSettings.editingAccount,
    isEmailValid: !state.pages.accountSettings.emailErrorMessage,
    useDefaultCustomer: state.pages.accountSettings.useDefaultCustomer,
    initialUseDefaultCustomer: state.pages.accountSettings.initialUseDefaultCustomer,
    pageTitle: getCurrentPage(state).fields.title,
    // xng-change
    hasChanges: hasChanges(state) || hasCustomAccountFieldsChanges(state),
    updatedAccount: state.xAccount,
});

const mapDispatchToProps = {
    saveCurrentAccount,
    setInitialValues,
    // xng-change
    xLoadingOverlay,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface AccountSettingsHeaderStyles {
    title: TypographyProps;
    titleWrapper: GridItemProps;
    buttonSetHidden?: HiddenProps;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    saveClickable?: ClickablePresentationProps;
    cancelClickable?: ClickablePresentationProps;
    buttonSet?: GridItemProps;
    // xng-change
    container?: GridContainerProps;
    errorMessageNotificationWrapper?: GridItemProps;
    errorMessageNotification?: InjectableCss;
}

export const headerStyles: AccountSettingsHeaderStyles = {
    saveButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    buttonSetHidden: {
        below: "md",
    },
    menuHidden: {
        above: "sm",
    },
    buttonSet: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 4, 3, 3],
    },
    titleWrapper: {
        width: [11, 11, 8, 9, 9],
    },
    title: {
        variant: "h1",
    },
};

const styles = headerStyles;

const AccountSettingsHeader = ({
    account,
    isEmailValid,
    hasChanges,
    pageTitle,
    useDefaultCustomer,
    saveCurrentAccount,
    setInitialValues,
    // xng-change
    updatedAccount,
    id,
    xLoadingOverlay,
}: Props) => {
    const [showMessage, setShowMessage] = useState(false);
    const [currentEmail, setCurrentEmail] = useState("");

    const toasterContext = useContext(ToasterContext);
    const updateSettingsHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        // xng-change
        xLoadingOverlay({ key: "add", id });
        saveCurrentAccount({
            onSuccess: () => {
                // xng-change
                xLoadingOverlay({ key: "clear", id });
                toasterContext.addToast({ body: translate("Settings updated"), messageType: "success" });
                if (currentEmail !== account?.email) {
                    setShowMessage(true);
                }
            },
            onError: (message: string) => {
                // xng-change
                xLoadingOverlay({ key: "clear", id });
                toasterContext.addToast({ body: message, messageType: "warning" });
            },
        });
    };

    const invalidCustomerState = useDefaultCustomer && !account?.defaultCustomerId;
    // xng-change
    const enableSaveButton = hasChanges && isEmailValid && !invalidCustomerState && updatedAccount?.isCustomFieldsValid;

    useEffect(() => {
        if (account && !currentEmail) {
            setCurrentEmail(account.email);
        } else if (showMessage) {
            setShowMessage(true);
        }
    }, [account?.email]);

    if (!account) {
        return null;
    }

    // xng-change: change structure
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.titleWrapper}>
                <Typography {...styles.title}>{pageTitle}</Typography>
            </GridItem>
            <GridItem {...styles.buttonSet}>
                {hasChanges && (
                    <Button {...styles.cancelButton} onClick={setInitialValues}>
                        {translate("Cancel")}
                    </Button>
                )}
                <Button
                    {...styles.saveButton}
                    onClick={updateSettingsHandler}
                    disabled={!enableSaveButton}
                    data-test-selector="accountSettings_save"
                >
                    {translate("Save")}
                </Button>
            </GridItem>
            {((showMessage && updatedAccount.properties?.resetPasswordRequired) ||
                (showMessage &&
                    currentEmail !== account.email &&
                    updatedAccount.properties?.resetPasswordRequired)) && (
                <GridItem {...styles.errorMessageNotificationWrapper}>
                    <Notify type="warning" {...styles.errorMessageNotification}>
                        <StyledWrapper>
                            {parse(updatedAccount.properties.resetPasswordRequired, xParserOptions)}
                        </StyledWrapper>
                    </Notify>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsHeader),
    definition: {
        group: "Account Settings",
        allowedContexts: ["AccountSettingsPage"],
        displayName: "Header",
    },
};

export default widgetModule;
